import {
  OreButton,
  OrePagination,
  OreStack,
  OreTable,
  OreTableActions,
  OreTableBody,
  OreTableCaption,
  OreTableCell,
  OreTableContainer,
  OreTableHead,
  OreTableRow,
  OreText,
  SvgArrowWidgetRight,
  SvgDownload,
} from '@edenredespana/oreneta'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { homeTranslation } from '@/Home/ui/homeTranslation'
import { invoicesTranslation } from '@/Invoices/ui/invoicesTranslation'
import { TableHeadersModel } from '@/Shared/domain/models/TableHeadersModel'
import { navigationRoutes } from '@/Shared/ui/routers'
import { tablePagination } from '@/Shared/ui/sharedTranslation'
import { TransactionModel } from '@/Transactions/domain'
import { useTransaction } from '@/Transactions/ui/context/TransactionsContext'
import { TransactionFilter, TransactionRow } from '@/Transactions/ui/molecules'
import { transactionsTranslation } from '@/Transactions/ui/transactionsTranslation'
import { useUserData } from '@/User/ui/context'

interface IProps {
  // Home usa una version resumida de la tabla (sin filtros/paginacion).
  isHome?: boolean
}

export const TransactionsList = ({ isHome }: IProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { merchant } = useUserData()
  const isIndependent = merchant.isIndependent
  const isNursery = merchant.isNursery

  const {
    transactions,
    clearTransactions,
    filters,
    handleSearch,
    count,
    page,
    numberOfRecordsPerPage,
    onPageChange,
    downloadTransactions,
  } = useTransaction()

  const transactionGuarderiaHeaders: TableHeadersModel[] = [
    {
      key: 'affiliateCode',
      label: transactionsTranslation.table.header.affiliateCode,
      size: 'auto',
    },
    ...(!merchant.isChild && isNursery && !isIndependent
      ? [
          {
            key: 'merchantName',
            label: invoicesTranslation.table.header.nursery,
            size: 'auto' as const,
          },
        ]
      : []),
    {
      key: 'liquidationDate',
      label: transactionsTranslation.table.header.liquidationDate,
      size: 'auto',
    },
    {
      key: 'kidName',
      label: transactionsTranslation.guarderia.kidName,
      size: 'auto',
    },
    {
      key: 'transactionAmount',
      label: transactionsTranslation.table.header.transactionAmount,
      size: 'auto',
    },
    {
      key: 'invoiceNumber',
      label: transactionsTranslation.table.header.invoiceNumberGuarderia,
      size: 'auto',
    },
  ]

  const transactionHeaders: TableHeadersModel[] = [
    {
      key: 'affiliateCode',
      label: transactionsTranslation.table.header.affiliateCode,
      size: 'auto',
    },
    {
      key: 'transactionDate',
      label: transactionsTranslation.table.header.transactionDate,
      size: 'auto',
    },
    {
      key: 'liquidationDate',
      label: transactionsTranslation.table.header.liquidationDate,
      size: 'auto',
    },
    {
      key: 'merchantName',
      label: transactionsTranslation.table.header.local,
      size: 'auto',
    },
    {
      key: 'transactionAmount',
      label: transactionsTranslation.table.header.transactionAmount,
      size: 'auto',
    },
    {
      key: 'invoiceNumber',
      label: transactionsTranslation.table.header.invoiceNumber,
      size: 'auto',
    },
    {
      key: 'product',
      label: transactionsTranslation.table.header.product,
      size: 'auto',
    },
    {
      key: 'service',
      label: transactionsTranslation.table.header.service,
      size: 'auto',
    },
  ]

  // En Home de guarderia ocultamos la columna de local para igualar el layout esperado.
  const headersToRender = merchant.isNursery
    ? isHome
      ? transactionGuarderiaHeaders.filter(header => header.key !== 'merchantName')
      : transactionGuarderiaHeaders
    : transactionHeaders

  // Home muestra un resumen (maximo 8 filas); fuera de Home se usa el paginado normal.
  const MAX_ROWS = 8
  const visibleTransactions = isHome
    ? transactions?.slice(0, Math.min(MAX_ROWS, numberOfRecordsPerPage ?? MAX_ROWS))
    : transactions?.slice(0, numberOfRecordsPerPage)

  return (
    <OreStack placeContent="stretch" space="2xlarge">
      <OreTableContainer>
        {/* En Home no se muestran filtros, solo en la vista completa de Transacciones. */}
        {!isHome ? (
          <TransactionFilter
            handleSearch={handleSearch}
            handleClear={clearTransactions}
            isNursery={merchant.isNursery}
          />
        ) : null}
        <OreTable>
          {/* Titulo de tarjeta en Home para mantener el mismo patron visual que Facturas. */}
          {isHome ? (
            <OreTableCaption title={t(homeTranslation.transactions.table.title)} />
          ) : null}
          <OreTableHead valign="middle">
            <OreTableRow hover={false}>
              {headersToRender.map((header: TableHeadersModel) => (
                <OreTableCell as="th" key={header.key} size={header.size}>
                  <span style={{ whiteSpace: 'pre-line', display: 'flex' }}>
                    {header.label && t(header.label)}
                  </span>
                </OreTableCell>
              ))}
            </OreTableRow>
          </OreTableHead>
          <OreTableBody valign="top">
            {visibleTransactions?.length ? (
              visibleTransactions.map((transaction: TransactionModel, index: number) => {
                return (
                  <TransactionRow
                    key={`p_${index}`}
                    transaction={transaction}
                    isNursery={isNursery}
                    isHome={isHome}
                    isIndependent={isIndependent}
                  />
                )
              })
            ) : (
              <OreTableRow valign="middle">
                <OreTableCell colSpan={headersToRender.length}>
                  <OreText size="text-md">
                    {filters?.find(
                      filter => filter.name === 'beginDate' && filter.value !== '',
                    ) // if filter contains dates
                      ? merchant.isNursery
                        ? t(transactionsTranslation.table.noTransactionsGuarderia)
                        : t(transactionsTranslation.table.noTransactions)
                      : t(transactionsTranslation.table.noFilters)}
                  </OreText>
                </OreTableCell>
              </OreTableRow>
            )}
          </OreTableBody>
        </OreTable>
        {/* En Home sustituimos la paginacion por CTA "Ver mas" a la pagina completa. */}
        {isHome && count > 0 ? (
          <OreTableActions>
            <OreButton
              category="highlight"
              icon={<SvgArrowWidgetRight />}
              iconAlign="right"
              onClick={() =>
                navigate(navigationRoutes.transactions, {
                  state: {
                    from: navigationRoutes.home,
                  },
                })
              }
            >
              {t(homeTranslation.transactions.table.viewMore)}
            </OreButton>
          </OreTableActions>
        ) : null}
        {/* La paginacion solo aplica en la vista completa de Transacciones. */}
        {!isHome && count > 0 ? (
          <OreTableActions>
            <OrePagination
              displayPageButtons
              count={count}
              labelNextPage={t(tablePagination.nextPageLabel)}
              labelPreviousPage={t(tablePagination.previousPageLabel)}
              onPageChange={onPageChange}
              page={page}
              rowsPerPage={numberOfRecordsPerPage}
              text={t(tablePagination.pageOf)}
              legend={t(tablePagination.rowsShowing)}
            />
          </OreTableActions>
        ) : null}
      </OreTableContainer>

      {/* Descarga masiva solo en la vista completa, no en el resumen de Home. */}
      {!isHome && count > 0 && (
        <div>
          <OreButton
            onClick={downloadTransactions}
            category="secondary"
            icon={<SvgDownload />}
          >
            {t(transactionsTranslation.buttons.downloadTransactions)}
          </OreButton>
        </div>
      )}
    </OreStack>
  )
}
